import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { PropertyCondition } from './property-condition';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class PropertyConditionService {

	statuses: any = [
		{ value: 1, name: 'Open' },
		{ value: 2, name: 'In Progress' },
		{ value: 3, name: 'Complete' },
		{ value: 4, name: 'HO Declined' },
		{ value: 5, name: 'Canceled' },
		{ value: 6, name: 'FYI' }
	];
	
	categories: any = [
		{ value: 1, name: 'Furnishing' },
		{ value: 2, name: 'Furniture' },
		{ value: 3, name: 'Maintenance' },
		{ value: 4, name: 'Remodel' },
		{ value: 5, name: 'Appliance' },
		{ value: 6, name: 'Fixture' },
	];
	
	urgencies : any = [
		{ value: 1, name: 'Recommended' },
		{ value: 2, name: 'Urgent' },
		{ value: 3, name: 'Required to Rent' }
	];
	
	rooms: any = [
		{ value: 1, name: 'Kitchen' },
		{ value: 2, name: 'Living Room' },
		{ value: 3, name: 'Dining Room' },
		{ value: 4, name: 'Den' },
		{ value: 5, name: 'Entry' },
		{ value: 6, name: 'Balcony' },
		{ value: 7, name: 'Yard' },
		{ value: 8, name: 'Exterior' },
		{ value: 9, name: 'Laundry' },
		{ value: 10, name: 'Bedroom1' },
		{ value: 11, name: 'Bedroom2' },
		{ value: 12, name: 'Bedroom3' },
		{ value: 13, name: 'Bedroom4' },
		{ value: 14, name: 'Bedroom5' },
		{ value: 15, name: 'Bedroom6' },
		{ value: 16, name: 'Bedroom7' },
		{ value: 17, name: 'Bedroom8' },
		{ value: 18, name: 'Bedroom9' },
		{ value: 19, name: 'Bathroom1' },
		{ value: 20, name: 'Bathroom2' },
		{ value: 21, name: 'Bathroom3' },
		{ value: 22, name: 'Bathroom4' },
		{ value: 23, name: 'Bathroom5' },
		{ value: 24, name: 'Bathroom6' },
		{ value: 25, name: 'Bathroom7' },
		{ value: 26, name: 'Bathroom8' },
		{ value: 27, name: 'Bathroom9' },
		{ value: 28, name: 'Billiard Room' },
		{ value: 29, name: 'Game Room' },
		{ value: 30, name: 'Other' },
		
	];
	
	reasons: any = [
		{ value: 1, name: 'End of life' },
		{ value: 2, name: 'Dated' },
		{ value: 3, name: 'Damaged' },
		{ value: 4, name: 'Health And Safety' },
	];
	
	outcomes: any = [
		{ value: 1, name: 'Increase Rev' },
		{ value: 2, name: 'Cure Deferred Maint' },
		{ value: 3, name: 'Mitigate Liability' },
		{ value: 4, name: 'Market Standard' },
	];
	
	visibilities: any = [
		{ value: 1, name: 'Internal' },
		{ value: 2, name: 'Homeowner' },
	];


	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'property-conditions';

	add(propertyCondition: PropertyCondition): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(propertyCondition));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, propertyCondition);
	}

	update(propertyCondition: PropertyCondition): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, propertyCondition);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));

	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}


	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
